import BigNumber from 'bignumber.js'

/**
 * 字符串填充「0」
 * @param {string} origin 原始字符串
 * @param {number} length 目标字符串长度
 * @param {boolean} isFillBack 是否从尾部跳槽
 * @returns {string} 处理后的字符串
 */
export function fillZero(origin, length, isFillBack) {
  length = origin.length > length ? origin.length : length // 若原长度比目标长度更长
  if (isFillBack) {
    return (origin.toString() + new Array(length).join('0')).substr(0, length)
  } else {
    return (new Array(length).join('0') + origin.toString()).substr(-length)
  }
}

// 获取小数位数
const getDecimalsLength = (number) => {
  const matched = String(number || 0).match(/\.(\d+)$/)
  return matched ? matched[1].length : 0
}
/**
 * 除去多余参数
 * @param {object} obj 需要处理的对象
 * @param {boolean} isFillEmptyStr 是否填充为空字符串
 * @returns {object} 处理完成后的对象
 */
export function deleteEmpty(obj, isFillEmptyStr = false) {
  obj = JSON.parse(JSON.stringify(obj))

  Object.keys(obj).forEach((key) => {
    if (typeof obj[key] === 'string') {
      obj[key] = obj[key].trim()
    }
    if (obj[key] === undefined || obj[key] === null || obj[key] === '') {
      if (isFillEmptyStr) {
        obj[key] = ''
      } else {
        delete obj[key]
      }
    }
    if (typeof obj[key] === 'object' && obj[key].constructor !== Array) {
      obj[key] = deleteEmpty(obj[key])
    }
  })
  return obj
}

/**
 * 时间类型兼容，兼容「时间戳数字」「时间戳字符串」和「时间对象」
 * @param {string|number|object} time 时间内容，可传入时间对象，或时间戳
 * @param {string} type 期望返回的数据类型
 * @returns {string|object} 时间对象或时间戳
 */
export function dealTime(time, type = 'string') {
  if (!time) {
    // eslint-disable-next-line no-constant-condition
    return (type = 'string' ? '' : null)
  }
  if (time.toString().indexOf('/') > 0 || time.toString().indexOf('-') > 0) {
    time = new Date(time)
  } else if (typeof time === 'string' || typeof time === 'number') {
    const standTimeStampLength = 13 // 标准时间戳长度
    time = fillZero(time, standTimeStampLength, true) // 转化为标准的13位时间戳
    time = new Date(parseInt(time))
  }
  if (type === 'object') {
    return time
  } else if (type === 'string') {
    return time.getTime()
  }
}

/**
 * 格式化时间
 * @param {string|number|object} date 时间内容，可传入时间对象，或时间戳
 * @param {string} formatStr 格式模板
 * @returns {string} 处理后的字符串
 */
export function formatTime(date, formatStr = 'YYYY-MM-DD hh:mm:ss') {
  if (!date) {
    return ''
  }
  date = dealTime(date, 'object')
  const formatType = {
    Y: date.getFullYear(),
    M: date.getMonth() + 1,
    D: date.getDate(),
    h: date.getHours(),
    m: date.getMinutes(),
    s: date.getSeconds(),
  }

  return formatStr.replace(/Y+|M+|D+|h+|m+|s+/g, (target) => (new Array(target.length).join('0') + formatType[target[0]]).substr(-target.length))
}

/**
 * 防抖
 * @param {function} func 回调函数
 * @param {number} waitTime 毫秒
 * @returns
 */

export function debounce(func, waitTime = 500) {
  let timeout

  return function () {
    let _this = this

    let args = arguments
    if (timeout) {
      clearTimeout(timeout)
    }

    timeout = setTimeout(() => {
      func.apply(_this, args)
    }, waitTime)
  }
}

// 获取映射信息
export function getKeyToValueMap(nameMap, key = 'value', value = 'label') {
  let list = Object.values(nameMap)
  let result = {}
  list.forEach((item) => {
    result[item[key]] = item[value]
  })
  return Object.freeze(result)
}

/**
 * 获取数组形式的 map 信息
 * @param {Object} nameMap 字典对象
 * @param {String} [keyName] 原来 Map 上的 key 作为数组元素的属性值时使用的 keyName
 * @param {Function} [sortMethod] 转换为数组时可选的排序方式，与 Array.sort 一致
 * @returns {Array} 数组形式的字典
 */
export function getMapToArray(nameMap, keyName = 'key', sortMethod = null) {
  let list = Object.entries(nameMap)
  let array = []
  list.forEach(([key, value]) => {
    const newItem = {
      [keyName]: key,
      ...value,
    }
    array.push(newItem)
  })

  if (sortMethod) {
    return array.sort(sortMethod)
  }
  return array
}

/**
 * 获取随机字符串
 * @param {number} length 字符串长度
 * @param {string} [type] 随机字符串类型，letter纯字母，number纯数字，mix字母与数字混合
 * @param {string} [prefix] 字符串前缀
 * @returns {string} 生成的随机字符串
 */
export function randomString(length, type = 'letter', prefix = '') {
  const numbers = '0123456789'
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'

  let chars = ''
  let result = prefix

  if (type === 'number' || type === 'mix') {
    chars += numbers
  }

  if (type === 'letter' || type === 'mix') {
    chars += letters
  }

  for (let i = 0; i < length; i++) {
    result += chars[Math.floor(Math.random() * chars.length)]
  }
  return result
}

/**
 * 格式化数字，每隔三位添加逗号
 * @param {number} num 数值
 * @returns {string} 格式化后的数值字符串
 */
export function parseNum(num) {
  const numberString = (num || 0).toString()
  // 在转换一些以万为单位的金额时，小数点位数会超过3位，小数点中的数字暂不解析
  const decimalPointIndex = numberString.indexOf('.')
  let intString = decimalPointIndex > -1 ? numberString.slice(0, decimalPointIndex) : numberString
  let decimalString = decimalPointIndex > -1 ? numberString.slice(decimalPointIndex) : ''
  let reg1 = new RegExp('(\\d)(?=(?:\\d{3})+($|\\.))', 'g')
  return intString.replace(reg1, '$1,') + decimalString
}

/**
 * 保留两位小数
 * @param {number|string} number 保留两位小数
 * @param {string} [mode=floor] 舍入模式，floor 是向下取整，round 是四舍五入，ceil 是向上进一位
 */
export const keep2Decimals = (number, mode = 'floor', decimals = 2) => {
  const times = Math.pow(10, decimals)
  const oneHundredTimes = new BigNumber(number).times(times)
  // https://mikemcl.github.io/bignumber.js/#constructor-properties
  const roundModes = {
    ceil: BigNumber.ROUND_CEIL, // 向正无限大方向舍入，始终不会减少计算值
    floor: BigNumber.ROUND_FLOOR, // 向负无限大方向舍入，始终不会增加计算值
    round: BigNumber.ROUND_HALF_UP, // 四舍五入
  }
  const roundMode = roundModes[mode]
  if (!Object.prototype.hasOwnProperty.call(roundModes, mode)) {
    throw new Error(`暂不支持 ${mode} 舍入模式`)
  }
  const integer = oneHundredTimes.integerValue(roundMode)
  const newNumber = new BigNumber(integer).dividedBy(times)
  return newNumber.toNumber()
}

/** 将元为单位的金额转为以万为单位 */
export function yuan2wan(price, options = { digits: 6, parseNumber: false, mode: 'floor' }) {
  let bigNumber = new BigNumber(price)
  let { digits, parseNumber, mode } = options
  if (digits === 4) {
    bigNumber = new BigNumber(bigNumber.integerValue(BigNumber.ROUND_FLOOR))
  } else if (digits === 6 && getDecimalsLength(bigNumber.toString()) > 2) {
    bigNumber = new BigNumber(keep2Decimals(bigNumber, mode))
  }
  const result = bigNumber.dividedBy(1e4).toNumber()
  if (!parseNumber) {
    return result
  }
  return parseNum(result)
}

/**
 * 判断是否为Null（不为0，undefined的情况下）
 * *用例: isNull(null) true
 * *用例: isNull(undefined) => false
 * *用例: isNull(0) => false
 * @param {any} data 任何的数据
 * @returns {Boolean} 该数据是否为Null
 */
export function isNull(data) {
  return !data && typeof data !== 'undefined' && data !== 0
}

/**
 * 根据 blob 或 url 直接下载文件
 * @param {string|Blob} url 获取文件对应的URL
 * @param {string} fileName 文件名
 * @param {string} fileType 文件类型
 */
export function download(url, fileName = '', fileType = '') {
  const isBlob = url instanceof Blob
  if (isBlob) {
    url = URL.createObjectURL(url)
  }
  const a = document.createElement('a')
  if (fileName && fileType) {
    a.download = `${fileName}.${fileType}`
  }
  a.href = url
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  a.remove()
  if (isBlob) {
    URL.revokeObjectURL(url)
  }
}
